import convertFilter from './convertFilter';
import { actions } from '../../redux';
import { customRequestStatuses, customRequestTypes, itemsOnPage } from './data';

import { useState, useCallback, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import { actions as usersActions } from 'features/users/redux';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useLocation } from 'react-router-dom';

const useCustomRequestsModel = () => {
  const dispatch = useDispatch();
  const locationRouter = useLocation();
  const { requests, actionProcessing, detailsData } = useSelector(state => state.creativeFunctionality, shallowEqual);
  const userFromURL = new URLSearchParams(locationRouter.search).get('username') ?? '';

  const [date, setDate] = useState({
    beginDate: new Date(dayjs().add(-1, 'month')),
    endDate: new Date(dayjs()),
  });

  const [values, setValues] = useState(() => {
    return {
      type: customRequestTypes[0],
      itemsOnPage,
      statuses: customRequestStatuses,
      page: 0,
    };
  });

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [filterValue, setFilterValue] = useState(userFromURL);
  const [searchResult, setSearchResult] = useState([]);
  const [isLoadingSearchResult, setIsLoadingSearchResult] = useState(false);
  const activeItemsOnPage = values.itemsOnPage.find(item => item.active);
  const pageCount = Math.ceil(requests?.count / Number(activeItemsOnPage.value)) ?? 1;

  const handlePageChange = value => {
    setValues(ps => ({
      ...ps,
      page: value,
    }));
  };

  const resetDetailsModal = () => {
    setIsModalOpen(false);
  };

  const handleDetails = id => {
    const callback = () => {
      setIsModalOpen(true);
    };

    dispatch(actions.getSingleCustomRequest(id, callback));
  };

  const debouncedSearchUser = useRef(
    debounce(value => {
      if (value.length >= 4) {
        setIsLoadingSearchResult(true);
        dispatch(
          usersActions.getUsersListForSearch({
            username: value,
            callback: data => {
              if (data) {
                setSearchResult(data.map(item => item.username));
              }
              setIsLoadingSearchResult(false);
            },
          }),
        );
      }
    }, 800),
  ).current;

  const handleSearchChange = e => {
    const { value } = e.target;
    setFilterValue(value);

    if (value.length >= 4) {
      setIsLoadingSearchResult(true);
      debouncedSearchUser(value);
    } else {
      setIsLoadingSearchResult(false);
      setSearchResult([]);
    }
  };

  const handleResultClick = username => {
    setFilterValue(username);
    setIsLoadingSearchResult(false);
    setSearchResult([]);
  };

  const onChangeBeginDate = value => {
    if (!isNaN(value.valueOf())) {
      setDate({ ...date, beginDate: value });
    }
  };

  const onChangeEndDate = value => {
    if (!isNaN(value.valueOf())) {
      setDate({ ...date, endDate: value });
    }
  };

  const onChangeStatuses = useCallback(
    e => {
      const { name } = e.target;

      setValues(ps => {
        const newStatuses = [...ps.statuses];
        const matchIdx = ps.statuses.findIndex(s => s.name === name);
        newStatuses.splice(matchIdx, 1, {
          ...ps.statuses[matchIdx],
          active: !ps.statuses[matchIdx].active,
        });

        return {
          ...ps,
          statuses: newStatuses,
        };
      });
    },
    [values],
  );

  const handleTypeChange = value => {
    const typeItem = customRequestTypes.find(item => item.value === value);
    setValues(ps => ({ ...ps, type: typeItem }));
  };

  const firstUpdate = useRef(!userFromURL);
  useEffect(() => {
    if (firstUpdate.current) {
      firstUpdate.current = false;
      return;
    }
    handleSearch();
  }, [pageCount, values.page]);

  const onClickSearch = () => {
    handleSearch();
    handlePageChange(0);
    setSearchResult([]);
  };

  const handleSearch = useCallback(() => {
    const username = filterValue.trim();
    const requestedData = {
      username,
      ...values,
      ...date,
    };
    dispatch(actions.getCustomRequests(convertFilter(requestedData)));
  }, [filterValue, values, date, dispatch]);

  const handleFormSubmit = e => {
    e.preventDefault();
    onClickSearch();
  };

  const handleKeyDown = e => {
    if (e?.key === 'Enter') {
      e.preventDefault();
      onClickSearch();
    }
  };

  useEffect(() => {
    if (filterValue.length < 4) setSearchResult([]);
  }, [filterValue, JSON.stringify(searchResult)]);

  useEffect(() => {
    handleSearch();
  }, []);

  useEffect(() => {
    if ((values.page > pageCount - 1 && pageCount - 1 >= 0) || pageCount === 0) handlePageChange(0);
  }, [values.page, pageCount]);

  return {
    input: {
      isModalOpen,
      setIsModalOpen,
      detailsData,
      date,
      values,
      filterValue,
      isLoadingSearchResult,
      searchResult,
      handleKeyDown,
      handlePageChange,
      handleFormSubmit,
      handleTypeChange,
      onChangeStatuses,
      onChangeEndDate,
      onChangeBeginDate,
      handleResultClick,
      handleSearchChange,
      handleDetails,
      resetDetailsModal,
      handleSearch,
    },
    output: {
      actionProcessing,
      requests,
      activeItemsOnPage,
    },
  };
};

export { useCustomRequestsModel };
